import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import styles from "../AuthWrapper.module.css";

import Logo from "../../../assets/Logo.svg";

import { showToast } from "../../../utils/toast";
import BaseLoading from "../../../components/Loader/BaseLoading";
import { resetPasswordTokenAPI } from "../../../api/userApi";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { Visibility, VisibilityOff } from "@material-ui/icons";

const ResetPassword = () => {
  const seo = useSEO("reset-password");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    // Check if token is present in URL
    if (!token) {
      showToast(
        "Invalid or missing password reset link. Please request a new one.",
        "error"
      );
      setTimeout(() => {
        history.push("/forgot-password");
      }, 2000);
      return;
    }

    // Allow form to be shown
    setLoading(false);
  }, [token, history]);

  // Password validation function
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(pwd)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(pwd)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(pwd)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
      return "Password must contain at least one special character (!@#$%^&*()_+-=[]{};':\"\\|,.<>/?";
    }
    return null;
  };

  const resetPassword = async () => {
    // Validate token
    if (!token) {
      showToast(
        "Invalid or missing password reset link. Please request a new one.",
        "error"
      );
      history.push("/forgot-password");
      return;
    }

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      showToast(passwordError, "error");
      return;
    }

    // Check password match
    if (password !== confirmPassword) {
      showToast("New password and confirm password do not match!", "error");
      return;
    }

    setSubmitting(true);

    try {
      const response = await resetPasswordTokenAPI({
        token,
        newPassword: password,
        confirmPassword: confirmPassword,
      });

      if (response?.success) {
        showToast(
          "Password has been reset successfully. You can now log in with your new password.",
          "success"
        );
        setTimeout(() => {
          history.push("/login");
        }, 2000);
      } else {
        showToast(
          response?.message || "Failed to reset password. Please try again.",
          "error"
        );
      }
    } catch (err: any) {
      console.error("Password reset error:", err);
      const errorMessage =
        err?.message ||
        err?.error ||
        "Invalid or expired password reset link. Please request a new one.";
      showToast(errorMessage, "error");

      // If token is invalid/expired, redirect to forgot password
      if (
        errorMessage.includes("invalid") ||
        errorMessage.includes("expired")
      ) {
        setTimeout(() => {
          history.push("/forgot-password");
        }, 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !submitting) {
      resetPassword();
    }
  };

  if (loading) {
    return <BaseLoading title="Loading..." />;
  }

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <div className={styles.authWrapper}>
        <div className={styles.authLeft}>
          <div className={styles.authBox}>
            <div
              className={styles.logoContainerReset}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={Logo} alt="SugarCribs" className={styles.logo} />
            </div>
            <h2 className={styles.title} style={{ textAlign: "center" }}>
              Create new password
            </h2>

            <label htmlFor="newPassword">
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 12,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                New Password
              </span>
              <div style={{ position: "relative" }}>
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={styles.inputField}
                  style={{ paddingRight: "50px" }}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPassword ? (
                    <VisibilityOff style={{ fontSize: "24px" }} />
                  ) : (
                    <Visibility style={{ fontSize: "24px" }} />
                  )}
                </button>
              </div>
            </label>

            <label
              htmlFor="confirmPassword"
              style={{ position: "relative", marginTop: 10 }}
            >
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 12,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                Confirm Password
              </span>
              <div style={{ position: "relative" }}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={styles.inputField}
                  style={{ paddingRight: "50px" }}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showConfirmPassword ? (
                    <VisibilityOff style={{ fontSize: "24px" }} />
                  ) : (
                    <Visibility style={{ fontSize: "24px" }} />
                  )}
                </button>
              </div>
            </label>

            <button
              className={styles.actionButton}
              onClick={resetPassword}
              disabled={submitting}
              style={{ marginTop: "24px" }}
            >
              {submitting ? "Resetting Password..." : "Reset Password"}
            </button>
          </div>
        </div>
        {/* <div className={styles.authPhoto}>
          <img src="/assets/AuthPhoto.svg" alt="Auth" />
        </div>
        <div className={styles.copyright}>
          © 2025 SugarCribs. All rights reserved.
        </div> */}
      </div>
    </>
  );
};

export default ResetPassword;
